import { useState, useEffect, useCallback } from 'react';
import { useAppRefresh } from '../hooks/useAppRefresh';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LunchWorkedApproval from '../components/timesheet/LunchWorkedApproval';
import { getToken } from '../services/api';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import PageHeader from '../components/PageHeader';
import {
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  User,
  Filter,
  Clock3,
  ChevronDown,
  ChevronUp,
  MapPin,
  X,
  Activity,
  Paperclip,
  Timer, Undo2 } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import leaveService, { type LeaveRequest } from '../services/leave';
import timesheetApprovalService, { type TimesheetApprovalRequest, type TimesheetEntry, type PublicHolidayInfo, type ScheduledDay, type LeaveDay } from '../services/timesheetApproval';
import { useModuleLicense } from '../hooks/useModuleLicense';
import { useOrgTimezone, formatTimeInTz, formatDateInTz } from '../utils/timezone';
import { toNum } from '@/utils/numeric';

// Fix Leaflet default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const clockInIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const clockOutIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

type AnyApproval =
  | (LeaveRequest & { _type: 'leave' })
  | (TimesheetApprovalRequest & { _type: 'timesheet' });

function sortApprovals(list: AnyApproval[]): AnyApproval[] {
  return [...list].sort((a, b) => {
    // Pending first
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (b.status === 'pending' && a.status !== 'pending') return 1;
    // Then newest submission first
    const aDate = new Date(a.submitted_at || a.created_at).getTime();
    const bDate = new Date(b.submitted_at || b.created_at).getTime();
    return bDate - aDate;
  });
}

function FitBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    // No animation: a zoom still in flight when the page unmounts throws from Leaflet.
    if (positions.length === 1) {
      map.setView(positions[0], 16, { animate: false });
    } else if (positions.length > 1) {
      map.fitBounds(L.latLngBounds(positions), { padding: [40, 40], animate: false });
    }
  }, [map, positions]);
  return null;
}

interface LocationMapModalProps {
  entry: TimesheetEntry;
  onClose: () => void;
}

function LocationMapModal({ entry, onClose }: LocationMapModalProps) {
  const inLat = toNum(entry.clock_in_latitude), inLng = toNum(entry.clock_in_longitude);
  const outLat = toNum(entry.clock_out_latitude), outLng = toNum(entry.clock_out_longitude);
  const positions: [number, number][] = [];
  if (inLat != null && inLng != null) {
    positions.push([inLat, inLng]);
  }
  if (outLat != null && outLng != null) {
    positions.push([outLat, outLng]);
  }

  const center: [number, number] = positions.length > 0 ? positions[0] : [-33.8688, 151.2093];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
          <h3 className="font-semibold text-neutral-900 text-sm">Clock Location</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="h-72">
          <MapContainer center={center} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds positions={positions} />
            {inLat != null && inLng != null && (
              <Marker position={[inLat, inLng]} icon={clockInIcon}>
                <Popup>
                  <strong>Clock In</strong>
                  {entry.clock_in_accuracy != null && <div className="text-xs text-neutral-500">±{Math.round(entry.clock_in_accuracy)}m accuracy</div>}
                </Popup>
              </Marker>
            )}
            {outLat != null && outLng != null && (
              <Marker position={[outLat, outLng]} icon={clockOutIcon}>
                <Popup>
                  <strong>Clock Out</strong>
                  {entry.clock_out_accuracy != null && <div className="text-xs text-neutral-500">±{Math.round(entry.clock_out_accuracy)}m accuracy</div>}
                </Popup>
              </Marker>
            )}
          </MapContainer>
        </div>
        <div className="px-4 py-2 bg-neutral-50 border-t border-neutral-200 flex gap-4 text-xs text-neutral-600">
          {entry.clock_in_latitude != null && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500"></span>
              Clock In
            </span>
          )}
          {entry.clock_out_latitude != null && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500"></span>
              Clock Out
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Approvals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const orgTimezone = useOrgTimezone();
  // Leave and Timesheet share one subscription. A single `approver` seat
  // covers both workflows; `hr` also grants approval rights.
  const { hasAccess: canAccessApprovals, loading: accessLoading } =
    useModuleLicense('timesheet', ['approver', 'hr']);
  // HR licence unlocks the org-wide "HR Override" tab (covering for an
  // unavailable supervisor).
  const { hasAccess: hasHrAccess } = useModuleLicense('timesheet', 'hr');
  const [approvals, setApprovals] = useState<AnyApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'hr'>('pending');
  const [processingKey, setProcessingKey] = useState<string | null>(null);
  const [expandedEntries, setExpandedEntries] = useState<Record<number, TimesheetEntry[]>>({});
  const [unworkedHolidays, setUnworkedHolidays] = useState<Record<number, PublicHolidayInfo[]>>({});
  const [scheduledByApproval, setScheduledByApproval] = useState<Record<number, { working_days: number; scheduled_hours: number; days: ScheduledDay[] }>>({});
  // Leave requests overlapping each approval's window — used so a scheduled
  // day covered by another leave type (pending or approved) isn't shown as
  // "Missed shift" in the supervisor view.
  const [leaveDaysByApproval, setLeaveDaysByApproval] = useState<Record<number, LeaveDay[]>>({});
  const [loadingEntries, setLoadingEntries] = useState<number | null>(null);
  const [locationEntry, setLocationEntry] = useState<TimesheetEntry | null>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);

  const [refreshTick, setRefreshTick] = useState(0);
  useAppRefresh(useCallback(() => setRefreshTick(t => t + 1), []));

  useEffect(() => {
    fetchApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, user?.organisation_id, refreshTick]);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      // HR Override tab: org-wide, regardless of supervisor — and deliberately
      // NOT limited to pending.
      //
      // HR is the only role that can reverse an approval, and the Reverse button
      // only renders on an item whose status is 'approved'. Asking for 'pending'
      // here meant that button had nothing to attach to: an HR holder who is not
      // the employee's own supervisor had no tab anywhere in the app that listed
      // an approved request, so the ability existed on the server and could not
      // be reached. Omitting the status returns pending AND approved, which is
      // what both endpoints do by default.
      const isHrTab = filter === 'hr';
      const statusParam = isHrTab
        ? undefined
        : filter === 'all' ? undefined : (filter as 'pending' | 'approved');
      const scopeParam: 'hr' | undefined = isHrTab ? 'hr' : undefined;

      const [leaveRes, timesheetRes] = await Promise.all([
        leaveService.getSupervisorApprovals({ status: statusParam, scope: scopeParam }),
        timesheetApprovalService.getSupervisorApprovals({ status: statusParam, scope: scopeParam }),
      ]);

      const leaveItems: AnyApproval[] = leaveRes.success
        ? leaveRes.data.map((r: LeaveRequest) => ({ ...r, _type: 'leave' as const }))
        : [];
      const timesheetItems: AnyApproval[] = timesheetRes.success
        ? timesheetRes.data.map((r: TimesheetApprovalRequest) => ({ ...r, _type: 'timesheet' as const }))
        : [];

      setApprovals(sortApprovals([...leaveItems, ...timesheetItems]));
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCertificate = async (requestId: number) => {
    try {
      const token = getToken();
      const response = await fetch(leaveService.getCertificateUrl(requestId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load certificate');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      console.error('Failed to open certificate');
    }
  };

  const handleApprove = async (request: AnyApproval) => {
    const key = `${request._type}-${request.id}`;
    try {
      setProcessingKey(key);
      setApprovalError(null);
      let response;
      if (request._type === 'leave') {
        response = await leaveService.reviewRequest(request.id, { status: 'approved' });
      } else {
        response = await timesheetApprovalService.reviewRequest(request.id, { status: 'approved' });
      }
      if (response.success) await fetchApprovals();
    } catch (error: any) {
      console.error('Failed to approve request:', error);
      if (error?.message?.includes('certificate')) {
        setApprovalError(`Cannot approve: a doctor's certificate is required but has not been uploaded.`);
      } else {
        setApprovalError(error?.message || 'Failed to approve request');
      }
    } finally {
      setProcessingKey(null);
    }
  };

  const handleReject = async (request: AnyApproval) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (!reason) return;

    const key = `${request._type}-${request.id}`;
    try {
      setProcessingKey(key);
      let response;
      if (request._type === 'leave') {
        response = await leaveService.reviewRequest(request.id, { status: 'rejected', rejection_reason: reason });
      } else {
        response = await timesheetApprovalService.reviewRequest(request.id, { status: 'rejected', rejection_reason: reason });
      }
      if (response.success) await fetchApprovals();
    } catch (error) {
      console.error('Failed to reject request:', error);
    } finally {
      setProcessingKey(null);
    }
  };

  /**
   * Reverse an approval that should not have been given.
   *
   * Until now an approval was final: the employee could not edit an approved
   * timesheet, nobody could un-approve it, and approved leave stayed on the
   * balance and the timesheet whatever had actually happened. The only way back
   * was a hand-written database change.
   *
   * Reversing leave also strips the timesheet rows approving it created and
   * credits the hours back; reversing a timesheet releases its entries for
   * editing and resubmission. Both are refused outright if payroll has already
   * finalised the period — that is a separate decision with its own route.
   */
  const handleReverse = async (request: AnyApproval) => {
    const what = request._type === 'leave' ? 'leave request' : 'timesheet';
    const confirmed = window.confirm(
      request._type === 'leave'
        ? 'Reverse this approved leave?\n\nThe leave entries will be removed from the '
          + 'timesheet and the hours credited back to the balance.'
        : 'Send this approved timesheet back?\n\nIts entries are released so the employee '
          + 'can correct and resubmit them.'
    );
    if (!confirmed) return;

    const reason = prompt(`Why is this approved ${what} being reversed?`);
    if (!reason) return;

    const key = `${request._type}-${request.id}`;
    try {
      setProcessingKey(key);
      setApprovalError(null);
      const response = request._type === 'leave'
        ? await leaveService.reviewRequest(request.id, { status: 'rejected', rejection_reason: reason })
        : await timesheetApprovalService.reviewRequest(request.id, { status: 'rejected', rejection_reason: reason });
      if (response.success) await fetchApprovals();
    } catch (error) {
      // The server's own wording carries the reason a reversal was refused —
      // a finalised pay period, most often — and that names the next step.
      setApprovalError(
        (error as { message?: string })?.message || `Failed to reverse this ${what}.`
      );
    } finally {
      setProcessingKey(null);
    }
  };

  const toggleEntries = async (request: TimesheetApprovalRequest) => {
    const id = request.id;
    if (expandedEntries[id]) {
      setExpandedEntries(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    try {
      setLoadingEntries(id);
      const response = await timesheetApprovalService.getApprovalEntries(id);
      if (response.success) {
        setExpandedEntries(prev => ({ ...prev, [id]: response.data }));
        setUnworkedHolidays(prev => ({
          ...prev,
          [id]: response.public_holidays_unworked || []
        }));
        setLeaveDaysByApproval(prev => ({
          ...prev,
          [id]: response.leave_days || []
        }));
        if (response.scheduled_working_days != null && response.scheduled_hours != null) {
          setScheduledByApproval(prev => ({
            ...prev,
            [id]: {
              working_days: response.scheduled_working_days as number,
              scheduled_hours: response.scheduled_hours as number,
              days: response.scheduled_days || [],
            }
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch timesheet entries:', error);
    } finally {
      setLoadingEntries(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getLeaveTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      annual: 'Annual Leave',
      personal: 'Personal/Sick Leave',
      unpaid: 'Unpaid Leave',
      other: 'Other',
      time_in_lieu: 'Time in Lieu',
      long_service: 'Long Service Leave',
    };
    return labels[type] || type;
  };

  // Gate the page on an active approver license (leave or timesheet).
  if (accessLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-neutral-500">
          Loading…
        </div>
      </div>
    );
  }
  if (!canAccessApprovals) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-lg text-neutral-600">
                You do not have an approver license. Contact your administrator to be assigned an Approver or HR seat.
              </p>
              <Button onClick={() => navigate('/dashboard')} className="mt-4">
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50">
      {locationEntry && (
        <LocationMapModal entry={locationEntry} onClose={() => setLocationEntry(null)} />
      )}
      <PageHeader
        title="Approvals"
        subtitle="Review and manage requests from your team"
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Live Dashboard shortcut */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/hr/live-dashboard')}
            className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5 hover:bg-emerald-100 transition-colors font-medium"
          >
            <Activity className="h-4 w-4" />
            View live attendance dashboard
          </button>
        </div>

        {/* Filter Buttons */}
        <div className="mb-6 flex items-start gap-3">
          <Filter className="h-5 w-5 shrink-0 text-neutral-500 mt-1.5" />
          <div className="flex flex-wrap gap-2">
            <Button variant={filter === 'pending' ? 'primary' : 'outline'} onClick={() => setFilter('pending')} size="sm">
              Pending
            </Button>
            <Button variant={filter === 'approved' ? 'primary' : 'outline'} onClick={() => setFilter('approved')} size="sm">
              Approved
            </Button>
            <Button variant={filter === 'all' ? 'primary' : 'outline'} onClick={() => setFilter('all')} size="sm">
              All
            </Button>
            {hasHrAccess && (
              <Button
                variant={filter === 'hr' ? 'primary' : 'outline'}
                onClick={() => setFilter('hr')}
                size="sm"
                className={filter === 'hr' ? '' : 'text-purple-700 border-purple-300 hover:bg-purple-50'}
                title="Approve any pending request org-wide on behalf of an unavailable supervisor"
              >
                HR Override
              </Button>
            )}
          </div>
        </div>

        {filter === 'hr' && (
          <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
            <strong>HR Override:</strong> showing every pending and approved request in the
            organisation. Approving notifies both the submitter and the original supervisor.
            An already-approved request can be <strong>reversed</strong> here — for leave that
            also removes its timesheet entries and credits the hours back.
          </div>
        )}

        {approvalError && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center justify-between">
            <span>{approvalError}</span>
            <button onClick={() => setApprovalError(null)} className="text-red-500 hover:text-red-700">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Approvals List */}
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-neutral-600">Loading approvals...</p>
            </CardContent>
          </Card>
        ) : approvals.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Calendar className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
              <p className="text-lg text-neutral-600">No approvals found</p>
              <p className="text-sm text-neutral-500 mt-2">
                {filter === 'pending'
                  ? 'There are no pending requests at this time.'
                  : filter === 'approved'
                  ? 'There are no approved requests.'
                  : filter === 'hr'
                  ? 'Nothing pending or approved anywhere in the organisation.'
                  : 'Your team has not submitted any requests.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {approvals.map((request) => {
              const processingThis = processingKey === `${request._type}-${request.id}`;
              return (
                <Card key={`${request._type}-${request.id}`} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      {/* Left side - Request details */}
                      <div className="flex-1 min-w-0">
                        {/* User + type badge row */}
                        <div className="flex items-start gap-3 mb-3">
                          <User className="h-5 w-5 text-neutral-500 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-0.5">
                              <p className="font-semibold text-neutral-900">{request.username}</p>
                              {request._type === 'leave' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                  <Calendar className="h-3 w-3" />
                                  Leave Request
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                                  <Clock3 className="h-3 w-3" />
                                  Timesheet
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-neutral-600 break-words">{request.email}</p>
                            {(filter === 'hr' || request.approved_on_behalf_of_username) && request.supervisor_username && (
                              <p className="text-xs text-neutral-500 mt-0.5">
                                Supervisor: <span className="font-medium text-neutral-700">{request.supervisor_username}</span>
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Details grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                          {request._type === 'leave' && (
                            <>
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Leave Type</p>
                                <p className="text-sm font-medium text-neutral-900">
                                  {getLeaveTypeLabel((request as LeaveRequest).leave_type)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Duration</p>
                                <p className="text-sm font-medium text-neutral-900">
                                  {formatDateInTz(request.start_date, orgTimezone)} – {formatDateInTz(request.end_date, orgTimezone)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Total Days</p>
                                <p className="text-sm font-medium text-neutral-900">
                                  {(request as LeaveRequest).total_days} {(request as LeaveRequest).total_days === 1 ? 'day' : 'days'}
                                </p>
                              </div>
                              {(request as LeaveRequest).scheduled_hours != null && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Scheduled Hours</p>
                                  <p className="text-sm font-medium text-neutral-900">
                                    {parseFloat((request as LeaveRequest).scheduled_hours as any).toFixed(2)}h
                                  </p>
                                </div>
                              )}
                              {(request as LeaveRequest).hours != null && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Requested Hours</p>
                                  <p className="text-sm font-medium text-neutral-900">
                                    {(request as LeaveRequest).hours}h
                                  </p>
                                </div>
                              )}
                              {/* Show the Certificate row whenever one is attached, OR when
                                  the leave type is one that can require a doctor's certificate.
                                  Sick certs are uploaded against `leave_type='personal'`. */}
                              {((request as LeaveRequest).certificate_file_name
                                || (request as LeaveRequest).leave_type === 'personal') && (
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Certificate</p>
                                  {(request as LeaveRequest).certificate_file_name ? (
                                    <button
                                      onClick={() => handleViewCertificate(request.id)}
                                      className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                                    >
                                      <Paperclip className="h-3.5 w-3.5" />
                                      View Certificate
                                    </button>
                                  ) : (
                                    <span className="text-sm font-medium text-amber-600">Not provided</span>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          {request._type === 'timesheet' && (
                            <>
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Period</p>
                                <p className="text-sm font-medium text-neutral-900">
                                  {formatDateInTz(request.start_date, orgTimezone)} – {formatDateInTz(request.end_date, orgTimezone)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Total Hours</p>
                                <p className="text-sm font-medium text-neutral-900">
                                  {parseFloat((request as TimesheetApprovalRequest).total_hours as any).toFixed(2)}h
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-neutral-500 mb-1">Entries</p>
                                <p className="text-sm font-medium text-neutral-900">
                                  {(request as TimesheetApprovalRequest).timesheet_entries_count} entries
                                </p>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Timesheet daily breakdown toggle */}
                        {request._type === 'timesheet' && (
                          <div className="mb-3">
                            <button
                              onClick={() => toggleEntries(request as TimesheetApprovalRequest)}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {loadingEntries === request.id ? (
                                <span>Loading...</span>
                              ) : expandedEntries[request.id] ? (
                                <><ChevronUp className="h-3 w-3" /> Hide daily breakdown</>
                              ) : (
                                <><ChevronDown className="h-3 w-3" /> View daily breakdown</>
                              )}
                            </button>

                            {expandedEntries[request.id] && (
                              <div className="mt-2 space-y-2">
                                {/* Scheduled hours / days for the period — gives the
                                    approver a quick comparison against submitted hours. */}
                                {scheduledByApproval[request.id] && (
                                  <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <span className="font-semibold uppercase tracking-wide text-blue-500">Scheduled</span>
                                    <span>
                                      <span className="font-semibold">{scheduledByApproval[request.id].working_days}</span>{' '}
                                      working {scheduledByApproval[request.id].working_days === 1 ? 'day' : 'days'}
                                    </span>
                                    <span className="text-blue-300">·</span>
                                    <span>
                                      <span className="font-semibold">{scheduledByApproval[request.id].scheduled_hours.toFixed(2)}h</span>{' '}
                                      rostered
                                    </span>
                                    <span className="text-blue-300">·</span>
                                    <span>
                                      Submitted <span className="font-semibold">{parseFloat((request as TimesheetApprovalRequest).total_hours as any).toFixed(2)}h</span>
                                    </span>
                                  </div>
                                )}
                                {/* Category totals summary */}
                                {(() => {
                                  const totals = expandedEntries[request.id].reduce<Record<string, number>>((acc, e) => {
                                    const cat = e.payroll_category || 'Base Hourly';
                                    acc[cat] = (acc[cat] || 0) + (parseFloat(e.total_hours as any) || 0);
                                    return acc;
                                  }, {});
                                  const cats = Object.entries(totals);
                                  if (cats.length === 0) return null;
                                  return (
                                    <div className="bg-white border border-neutral-200 rounded-lg p-3">
                                      <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2">Hours by Category</p>
                                      <div className="flex flex-wrap gap-2">
                                        {cats.map(([cat, hours]) => (
                                          <div
                                            key={cat}
                                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${
                                              cat === 'Public Holiday' || cat === 'Public Holiday Worked'
                                                ? 'bg-emerald-100 text-emerald-800'
                                                : cat.startsWith('Overtime')
                                                ? 'bg-amber-100 text-amber-800'
                                                : cat === 'Time in Lieu'
                                                ? 'bg-purple-100 text-purple-800'
                                                : 'bg-neutral-100 text-neutral-700'
                                            }`}
                                          >
                                            <span>{cat}</span>
                                            <span className="font-bold ml-1">{hours.toFixed(2)}h</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })()}
                                {/* Scheduled days the employee did NOT clock — split into:
                                    - covered by a leave request (pending or approved) → leave chip
                                    - genuinely missed (no entry, no leave) → red "Missed shift" */}
                                {(() => {
                                  const sched = scheduledByApproval[request.id];
                                  if (!sched || sched.days.length === 0) return null;
                                  const workedDates = new Set(
                                    expandedEntries[request.id].map(e => e.date)
                                  );
                                  const leaveByDate = new Map<string, LeaveDay>();
                                  for (const ld of leaveDaysByApproval[request.id] || []) {
                                    // Prefer 'approved' over 'pending' if both exist for the same date.
                                    const existing = leaveByDate.get(ld.date);
                                    if (!existing || (existing.status === 'pending' && ld.status === 'approved')) {
                                      leaveByDate.set(ld.date, ld);
                                    }
                                  }
                                  const uncovered = sched.days.filter(d => !workedDates.has(d.date));
                                  if (uncovered.length === 0) return null;
                                  return uncovered.map(d => {
                                    const leave = leaveByDate.get(d.date);
                                    if (leave) {
                                      const isPending = leave.status === 'pending';
                                      const label = getLeaveTypeLabel(leave.leave_type);
                                      return (
                                        <div
                                          key={`leave-${d.date}`}
                                          className={`border rounded-lg p-3 flex items-center justify-between ${
                                            isPending
                                              ? 'border-amber-200 bg-amber-50'
                                              : 'border-blue-200 bg-blue-50'
                                          }`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <Calendar className={`h-4 w-4 ${isPending ? 'text-amber-600' : 'text-blue-600'}`} />
                                            <span className={`text-sm font-semibold ${isPending ? 'text-amber-900' : 'text-blue-900'}`}>
                                              {formatDateInTz(`${d.date}T00:00:00Z`, orgTimezone)}
                                            </span>
                                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                              isPending ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                                            }`}>
                                              {isPending ? `Pending leave: ${label}` : label}
                                            </span>
                                          </div>
                                          <span className={`text-xs ${isPending ? 'text-amber-700' : 'text-blue-700'}`}>
                                            Scheduled {d.scheduled_hours.toFixed(2)}h ({d.start_time}–{d.end_time})
                                          </span>
                                        </div>
                                      );
                                    }
                                    return (
                                      <div
                                        key={`missed-${d.date}`}
                                        className="border border-red-200 rounded-lg p-3 bg-red-50 flex items-center justify-between"
                                      >
                                        <div className="flex items-center gap-2">
                                          <Calendar className="h-4 w-4 text-red-600" />
                                          <span className="text-sm font-semibold text-red-900">
                                            {formatDateInTz(`${d.date}T00:00:00Z`, orgTimezone)}
                                          </span>
                                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                            Missed shift
                                          </span>
                                        </div>
                                        <span className="text-xs text-red-700">
                                          Scheduled {d.scheduled_hours.toFixed(2)}h ({d.start_time}–{d.end_time})
                                        </span>
                                      </div>
                                    );
                                  });
                                })()}
                                {/* Public holidays in the period that the employee wasn't scheduled for */}
                                {(unworkedHolidays[request.id] || []).map(ph => (
                                  <div
                                    key={`ph-${ph.date}`}
                                    className="border border-emerald-200 rounded-lg p-3 bg-emerald-50 flex items-center justify-between"
                                  >
                                    <div className="flex items-center gap-2">
                                      <Calendar className="h-4 w-4 text-emerald-600" />
                                      <span className="text-sm font-semibold text-emerald-900">
                                        {formatDateInTz(`${ph.date}T00:00:00Z`, orgTimezone)}
                                      </span>
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                                        {ph.name}
                                      </span>
                                    </div>
                                    <span className="text-xs text-emerald-700 italic">Not scheduled</span>
                                  </div>
                                ))}
                                {expandedEntries[request.id].length === 0 && (unworkedHolidays[request.id] || []).length === 0 ? (
                                  <p className="text-xs text-neutral-400 text-center py-3">No entries found</p>
                                ) : (
                                  expandedEntries[request.id].map(entry => {
                                    const hasLocation = entry.clock_in_latitude != null || entry.clock_out_latitude != null;
                                    const isPublicHoliday = entry.payroll_category === 'Public Holiday' || !!entry.holiday_name;
                                    // Hours the employee claimed off their roster because they never
                                    // clocked — the times below are scheduled, not recorded.
                                    const isMissedClock = entry.missed_clock === true;
                                    return (
                                      <div
                                        key={entry.id}
                                        className={`border rounded-lg p-3 ${
                                          isPublicHoliday
                                            ? 'border-emerald-200 bg-emerald-50'
                                            : isMissedClock
                                              ? 'border-indigo-300 bg-indigo-50'
                                              : 'border-neutral-200 bg-neutral-50'
                                        }`}
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <span className={`text-sm font-semibold ${isPublicHoliday ? 'text-emerald-900' : 'text-neutral-800'}`}>
                                            {formatDateInTz(entry.clock_in_time, orgTimezone)}
                                            {entry.holiday_name && (
                                              <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                                                {entry.holiday_name}
                                              </span>
                                            )}
                                          </span>
                                          <div className="flex items-center gap-2">
                                            {entry.total_hours != null && (
                                              <span className="text-sm font-bold text-neutral-700">
                                                {parseFloat(entry.total_hours as any).toFixed(2)}h
                                              </span>
                                            )}
                                            {hasLocation ? (
                                              <button
                                                onClick={() => setLocationEntry(entry)}
                                                className="text-blue-500 hover:text-blue-700"
                                                title="View location on map"
                                              >
                                                <MapPin className="h-4 w-4" />
                                              </button>
                                            ) : (
                                              <MapPin className="h-4 w-4 text-neutral-300" aria-label="No location recorded" />
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-neutral-600 mb-2">
                                          <span className="flex items-center gap-1">
                                            <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                                            {formatTimeInTz(entry.clock_in_time, orgTimezone)}
                                          </span>
                                          <span className="text-neutral-300">→</span>
                                          <span className="flex items-center gap-1">
                                            <span className="inline-block w-2 h-2 rounded-full bg-red-400"></span>
                                            {entry.clock_out_time
                                              ? formatTimeInTz(entry.clock_out_time, orgTimezone)
                                              : <span className="text-amber-500 font-medium">Active</span>}
                                          </span>
                                          {isMissedClock && (
                                            <span className="text-indigo-600 italic">scheduled times</span>
                                          )}
                                        </div>
                                        <div className="mb-1">
                                          {entry.payroll_category === 'Public Holiday' ? (
                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                                              Public Holiday
                                            </span>
                                          ) : entry.payroll_category === 'Overtime (1.5x)' ? (
                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                              {entry.payroll_category}
                                            </span>
                                          ) : entry.payroll_category && entry.payroll_category !== 'Base Hourly' ? (
                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                              {entry.payroll_category}
                                            </span>
                                          ) : (
                                            <span className="text-xs text-neutral-400">
                                              {entry.payroll_category || 'Base Hourly'}
                                            </span>
                                          )}
                                        </div>
                                        {isMissedClock && (
                                          <p className="text-xs text-indigo-700 mt-1 font-medium flex items-start gap-1">
                                            <Timer className="h-3 w-3 mt-0.5 shrink-0" />
                                            <span>
                                              No clock in/out recorded — employee claimed their scheduled hours
                                              {entry.missed_clock_reason ? `: ${entry.missed_clock_reason}` : ''}
                                            </span>
                                          </p>
                                        )}
                                        {entry.skip_lunch_deduction && (
                                          <LunchWorkedApproval
                                            entryId={entry.id}
                                            approvedByName={entry.lunch_worked_approved_by_name}
                                            approvedAt={entry.lunch_worked_approved_at}
                                            canEdit={request.status === 'pending'}
                                            orgTimezone={orgTimezone}
                                            myName={user?.username || 'you'}
                                            onChange={(next) => setExpandedEntries(prev => ({
                                              ...prev,
                                              [request.id]: (prev[request.id] || []).map(e => e.id === entry.id
                                                ? { ...e, lunch_worked_approved_at: next.approvedAt, lunch_worked_approved_by_name: next.approvedByName }
                                                : e),
                                            }))}
                                          />
                                        )}
                                        {entry.notes && (
                                          <p className="text-xs text-neutral-500 mt-1 italic">{entry.notes}</p>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {request.notes && (
                          <div className="mb-3">
                            <p className="text-xs text-neutral-500 mb-1">Notes</p>
                            <p className="text-sm text-neutral-700 bg-neutral-50 p-2 rounded">{request.notes}</p>
                          </div>
                        )}

                        {'reason' in request && request.reason && (
                          <div className="mb-3">
                            <p className="text-xs text-neutral-500 mb-1">Reason</p>
                            <p className="text-sm text-neutral-700 bg-neutral-50 p-2 rounded">{request.reason}</p>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-xs text-neutral-500">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>Submitted {formatDateInTz(request.submitted_at || request.created_at, orgTimezone)}</span>
                          </div>
                          {request.approved_by_username && (
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              <span>
                                Reviewed by {request.approved_by_username}
                                {request.approved_on_behalf_of_username && (
                                  <span className="ml-1 text-purple-700 font-medium">
                                    (HR override — on behalf of {request.approved_on_behalf_of_username})
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status badge + action buttons */}
                      <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-start gap-3 pt-1 md:pt-0 md:min-w-[120px]">
                        {getStatusBadge(request.status)}

                        {request.status === 'pending' && (
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleApprove(request)}
                              disabled={processingThis}
                              className="flex items-center gap-1 text-green-600 hover:bg-green-50 hover:border-green-200"
                            >
                              <CheckCircle className="h-4 w-4" />
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleReject(request)}
                              disabled={processingThis}
                              className="flex items-center gap-1 text-red-600 hover:bg-red-50 hover:border-red-200"
                            >
                              <XCircle className="h-4 w-4" />
                              Reject
                            </Button>
                          </div>
                        )}

                        {request.status === 'approved' && hasHrAccess && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReverse(request)}
                            disabled={processingThis}
                            className="flex items-center gap-1 text-amber-700 hover:bg-amber-50 hover:border-amber-200"
                          >
                            <Undo2 className="h-4 w-4" />
                            Reverse
                          </Button>
                        )}
                      </div>
                    </div>

                    {request.rejection_reason && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                        <p className="text-xs text-red-600 font-medium mb-1">Rejection Reason:</p>
                        <p className="text-sm text-red-700">{request.rejection_reason}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export default Approvals;
